/**
 * Story 29.3 — Settings › Notifications on the harness.
 *
 *   GET /api/settings/notifications                  every role
 *   PUT /api/settings/notifications/contacts/:scope  owner (or superuser)
 *   PUT /api/settings/notifications/push-paused      owner (or superuser)
 *
 * Story 30.5 — my own quiet hours (every role, self only: the harness takes
 * the user from the JWT):
 *   GET /api/settings/notifications/quiet-hours
 *   PUT /api/settings/notifications/quiet-hours  { enabled, start, end, timeZone } → 204
 *     400 INVALID_BODY · 422 INVALID_QUIET_HOURS (start = end) · 422 INVALID_TIME_ZONE
 *
 * `:scope` is `tenant` (the tenant default contact) or an enabled plugin id.
 * `{ userId: null }` clears a contact. Every successful PUT answers 204.
 * Through the shared `harnessClient` (bearer attach + 401 refresh replay).
 */

import { harnessClient } from '@/lib/api/client';

export const NOTIFICATION_SETTINGS_PATH = '/api/settings/notifications';
/** The tenant default scope in `PUT …/contacts/:scope`. */
export const TENANT_CONTACT_SCOPE = 'tenant';

export interface NotificationPerson {
  userId: string;
  name: string;
  role: string;
}

export interface PluginContactSetting {
  pluginId: string;
  pluginName: string;
  userId: string | null;
  eligibleUserIds: string[];
}

export interface NotificationSettings {
  canEdit: boolean;
  /** No tenant contact, or that person is no longer eligible. */
  needsTenantContact: boolean;
  pushPaused: boolean;
  tenantContact: string | null;
  pluginContacts: PluginContactSetting[];
  people: NotificationPerson[];
}

export async function fetchNotificationSettings(
  signal?: AbortSignal,
  options: { skipErrorToast?: boolean } = {},
): Promise<NotificationSettings> {
  const res = await harnessClient.get<NotificationSettings>(NOTIFICATION_SETTINGS_PATH, {
    signal,
    skipErrorToast: options.skipErrorToast,
  });
  return res.data;
}

/** `scope` = `TENANT_CONTACT_SCOPE` or a plugin id; `userId` null clears it. */
export async function putNotificationContact(scope: string, userId: string | null): Promise<void> {
  await harnessClient.put(
    `${NOTIFICATION_SETTINGS_PATH}/contacts/${encodeURIComponent(scope)}`,
    { userId },
  );
}

export async function putPushPaused(paused: boolean): Promise<void> {
  await harnessClient.put(`${NOTIFICATION_SETTINGS_PATH}/push-paused`, { paused });
}

/** Story 30.5 — `GET …/quiet-hours`. Times are local `HH:MM` in the zone. */
export interface QuietHours {
  /** False until the person saves once (the values are then the default). */
  saved: boolean;
  enabled: boolean;
  start: string;
  end: string;
  /** The stored IANA zone; null = the organisation's zone. */
  timeZone: string | null;
  /** The zone actually used: the stored one, or the organisation's. */
  effectiveTimeZone: string;
}

export interface QuietHoursInput {
  enabled: boolean;
  start: string;
  end: string;
  timeZone: string | null;
}

export const QUIET_HOURS_PATH = `${NOTIFICATION_SETTINGS_PATH}/quiet-hours`;

export async function fetchQuietHours(signal?: AbortSignal): Promise<QuietHours> {
  const res = await harnessClient.get<QuietHours>(QUIET_HOURS_PATH, { signal, skipErrorToast: true });
  return res.data;
}

export async function putQuietHours(input: QuietHoursInput): Promise<void> {
  await harnessClient.put(QUIET_HOURS_PATH, {
    enabled: input.enabled,
    start: input.start,
    end: input.end,
    timeZone: input.timeZone,
  }, { skipErrorToast: true });
}
